import { createSlice, createAction, PayloadAction } from '@reduxjs/toolkit';
import { ChangeMemberStatusParams, GetMeetingByUserId } from 'api/meeting';
import { getStaffId } from 'data/storage';
import {
  AttendState,
  Meeting,
  MeetingRecord,
  OrderMeetingV2,
  OrderMembers,
  MeetingFile,
  MeetingSummary,
  MeetingIotMsg,
  IMeetingCalendarDetailList,
  IMeetingOrderForm,
  OrderSettings
} from 'type/meeting';
import { IMeetingDetail, MeetingOptionType } from 'type/meetingDetail';
import { RoomSchedule } from 'type/room';

// 模型定义, 该用户相关的操作和数据
type InitialState = {
  themeConfig: any; // 已废弃
  meetingInfo: Meeting | null;
  meetingDetailV2: IMeetingDetail | null;
  meetingFiles: MeetingFile[];
  meetingRecords: MeetingRecord[];
  meetingRecordsLoading: boolean;
  calendarMeetingRecords: MeetingRecord[];
  meetingSummary: MeetingSummary | null;
  // 该会议是否已操作过继续会议（用于会议提前结束）
  hasContinueOperation: boolean | null;
  meetingCalendarDetailList: IMeetingCalendarDetailList[];
  meetingCalendarLoading: boolean;
  // 跳转到会议预约页携带的参数
  meetingOrderParams: Partial<IMeetingOrderForm> | null;
  // 预约配置（读取管理后台配置）
  orderSettings: null | OrderSettings;
  // 冲突日程（用于 预约会议、修改会议 冲突显示）
  conflictSchedules: RoomSchedule[];
  recentlyList: any[];
};

export enum Handles {
  CREATE = 'create',
  DETELE = 'delete',
  CANCEL = 'cancel',
  QUIT = 'quit',
  CLOSE = 'close',
  CHANGEATTENDSTATE = 'changeAttendState',
  UPDATE = 'update'
}

const initialState = {
  themeConfig: null,
  meetingInfo: null,
  meetingDetailV2: null,
  meetingFiles: [],
  meetingRecords: [],
  meetingRecordsLoading: false,
  calendarMeetingRecords: [],
  meetingSummary: null,
  hasContinueOperation: null,
  meetingCalendarDetailList: new Array(42),
  meetingCalendarLoading: false,
  // 跳转到会议预约页携带的参数
  meetingOrderParams: null,
  orderSettings: null,
  conflictSchedules: [],
  recentlyList: [],
} as InitialState;

const meeting = createSlice({
  name: 'meeting',
  initialState: initialState,
  reducers: {
    setRecentlyList(state, action: PayloadAction<any>) {
      state.recentlyList = action.payload;
    },
    setMeetingThemeConfig(state, action: PayloadAction<any>) {
      state.themeConfig = action.payload;
    },
    setMeetingInfo(state, action: PayloadAction<Meeting | null>) {
      state.meetingInfo = action.payload;
    },
    setMeetingDetailV2(state, action: PayloadAction<IMeetingDetail | null>) {
      state.meetingDetailV2 = action.payload;
    },
    setMeetingFiles(state, action: PayloadAction<MeetingFile[]>) {
      state.meetingFiles = action.payload;
    },
    updateMeetingFile(state, action: PayloadAction<MeetingFile>) {
      const newFile = action.payload;
      state.meetingFiles = state.meetingFiles.map((file) => {
        if (newFile._id !== file._id) return file;
        return newFile;
      });
    },
    setMeetingFileByList(state, action: PayloadAction<MeetingFile[]>) {
      // 已上传成功的文件列表
      const list = action.payload || [];
      if (!Array.isArray(list)) {
        return;
      }
      // 从所有文件中查找不在成功列表的文件
      const newFiles = state.meetingFiles.filter(
        (d) => d.formData !== null && d.status !== 'success' && !list.find((l) => l.itemId === d.itemId)
      ) as MeetingFile[];
      state.meetingFiles = newFiles.concat(
        list
          .map((d) => {
            return {
              _id: d.itemId,
              itemId: d.itemId,
              status: 'success',
              name: d.name,
              contentType: d.contentType,
              size: d.size,
              percent: 100,
              formData: null,
              createdDate: d.createdDate,
              url: d.url
            } as MeetingFile;
          })
          .sort((a, b) => a.createdDate - b.createdDate)
      );
    },
    chgAttendState(state, action: PayloadAction<AttendState>) {
      if (!state.meetingInfo) return;
      if (!state.meetingInfo.members || !state.meetingInfo.members.length) return;
      const staffId = getStaffId();

      const i = state.meetingInfo.members.findIndex((m) => m.user.staffId === staffId);

      if (i > -1) state.meetingInfo.members[i].state = action.payload;
    },
    setMeetingRecord(state, action: PayloadAction<MeetingRecord[]>) {
      state.meetingRecords = action.payload;
    },
    setMeetingRecordsLoading(state, action: PayloadAction<boolean>) {
      state.meetingRecordsLoading = action.payload;
    },
    setCalendarMeetingRecords(state, action: PayloadAction<MeetingRecord[]>) {
      state.calendarMeetingRecords = action.payload;
    },
    setMeetingOrderState(state, action: PayloadAction<number>) {
      if (state.meetingInfo) {
        state.meetingInfo = { ...state.meetingInfo, orderState: action.payload };
      }
    },
    setMeetingSummary(state, action: PayloadAction<MeetingSummary | null>) {
      state.meetingSummary = action.payload;
    },
    setIotMessage(state, action: PayloadAction<MeetingIotMsg[]>) {
      if (state.meetingInfo) {
        state.meetingInfo = { ...state.meetingInfo, iotMessage: action.payload };
      }
    },
    setHasContinueOperation(state, action: PayloadAction<boolean>) {
      state.hasContinueOperation = action.payload;
    },
    setMeetingCalendarDetailList(state, action: PayloadAction<IMeetingCalendarDetailList[]>) {
      state.meetingCalendarDetailList = action.payload;
    },
    setMeetingCalendarLoading(state, action: PayloadAction<boolean>) {
      state.meetingCalendarLoading = action.payload;
    },
    setMeetingOrderParams(state, action: PayloadAction<Partial<IMeetingOrderForm | null>>) {
      state.meetingOrderParams = action.payload ? { ...state.meetingOrderParams, ...action.payload } : null;
    },
    resetMeetingOrderParams(state) {
      state.meetingOrderParams = null;
    },
    setOrderSettings(state, action: PayloadAction<OrderSettings>) {
      state.orderSettings = action.payload;
    },
    setConflictSchedules(state, action: PayloadAction<RoomSchedule[]>) {
      state.conflictSchedules = action.payload;
    },
    resetConflictSchedules(state) {
      state.conflictSchedules = [];
    }
  }
});

export default meeting.reducer;

export const updateMeeting = createAction<{ meetingId: string; params: OrderMeetingV2; type?: MeetingOptionType; repeatMeetingNo?: string; redirect?: boolean; onSuccess?:() => void; onFail?: (err: unknown) => void }>('meeting/updateMeeting');
export const orderMeeting = createAction<{ meeting: OrderMeetingV2; redirect?: boolean; onSuccess?:(meeting: IMeetingDetail) => void; onFail?: (err: unknown) => void } >('meeting/orderMeeting');
export const getMeetingDetails = createAction<string>('meeting/getMeetingDetails');
export const getMeetingDetailsV2 = createAction<string>('meeting/getMeetingDetailsV2');
export const deleteMeeting = createAction<string>('meeting/deleteMeeting');
export const quitMeeting = createAction<string>('meeting/quitMeeting');
export const changeAttendState = createAction<ChangeMemberStatusParams>('meeting/changeAttendState');
export const addMeetingMember = createAction<{ meetingId: string; members: OrderMembers }>('meeting/addMeetingMember');
export const deleteMeetingMember = createAction<{ meetingId: string; members: { ids: string[] } }>('meeting/deleteMeetingMember');
export const uploadFile = createAction<MeetingFile[]>('meeting/uploadFile');
export const getFilesInfo = createAction<{ shareId: string; accessKey: string }>('meeting/getFilesInfo');
export const addMeetingFile = createAction<{ meetingId: string; shareId: string; accessKey: string; fileList: MeetingFile[] }>('meeting/addMeetingFile');
export const deleteMeetingFile = createAction<{ meetingId: string; shareId: string; accessKey: string; fileIds: string[] }>('meeting/deleteMeetingFile');
export const cancelMeeting = createAction<Meeting>('meeting/cancel');
export const cancelMeetingV2 = createAction<string>('meeting/cancelV2');
export const cancelThisRepeatMeeting = createAction<string>('meeting/cancelThisRepeat');
export const cancelRepeatMeeting = createAction<{ repeatMeetingNo: string; startTimeGe?: number }>('meeting/cancelAllRepeat');
export const revokeMeeting = createAction<string>('meeting/revoke');
export const closeMeeting = createAction<Meeting>('meeting/close');
export const closeMeetingV2 = createAction<string>('meeting/closeV2');
export const getUsersMeetings = createAction<GetMeetingByUserId>('meeting/usersMeeting');
export const getCalendarMeetings = createAction<GetMeetingByUserId>('meeting/getCalendarMeeting');
export const getMeetingSummary = createAction<string>('meeting/getMeetingSummary');
export const publishMeetingSummary = createAction<{ meetingId: string; subject: string; content: string; staffId: string }>('meeting/publishMeetingSummary');
export const publishMeetingContent = createAction<{ meetingId: string; content: string; }>('meeting/publishMeetingContent');
export const getMeetingIotDevices = createAction<string>('meeting/getMeetingIotDevices');
export const continueMeeting = createAction<string>('meeting/continueMeeting');
export const getContinueMeetingOperation = createAction<string>('meeting/getContinueMeetingOperation');
export const getMeetingThemeConfig = createAction<string>('meeting/getMeetingThemeConfig');
export const getMeetingCalendarDetailList = createAction<GetMeetingByUserId>('meeting/getMeetingCalendarDetailList');
export const getOrderSettings = createAction('meeting/orderSettings');
export const getRecentlyList = createAction('meeting/getRecentlyList');

export const {
  setMeetingThemeConfig,
  setMeetingInfo,
  setMeetingDetailV2,
  setMeetingFiles,
  setMeetingFileByList,
  updateMeetingFile,
  chgAttendState,
  setMeetingRecord,
  setMeetingRecordsLoading,
  setCalendarMeetingRecords,
  setMeetingOrderState,
  setMeetingSummary,
  setIotMessage,
  setHasContinueOperation,
  setMeetingCalendarDetailList,
  setMeetingCalendarLoading,
  setMeetingOrderParams,
  resetMeetingOrderParams,
  setOrderSettings,
  setConflictSchedules,
  resetConflictSchedules,
  setRecentlyList,
} = meeting.actions;
